import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import styles from './Input.module.css';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: ReactNode;
  leftAdornment?: ReactNode;
  rightAdornment?: ReactNode;
  containerClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      leftAdornment,
      rightAdornment,
      id,
      className = '',
      containerClassName = '',
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? `loop-input-${generatedId}`;
    const hasError = Boolean(error);

    const wrapperClass = [styles.inputWrapper, hasError ? styles.error : '']
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`${styles.field} ${containerClassName}`.trim()}>
        {label && (
          <label htmlFor={inputId} className={`${styles.label} body-small-medium`}>
            {label}
          </label>
        )}
        <div className={wrapperClass}>
          {leftAdornment && <span className={styles.adornment}>{leftAdornment}</span>}
          <input
            id={inputId}
            ref={ref}
            className={`${styles.input} ${className}`.trim()}
            aria-invalid={hasError || undefined}
            {...props}
          />
          {rightAdornment && <span className={styles.adornment}>{rightAdornment}</span>}
        </div>
        {hasError && (
          <span className={`${styles.errorText} body-xsmall-medium`} role="alert">
            {error}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
